import { ParsedUrlQuery } from 'querystring'

import { GetStaticProps } from 'next'
import { Colors, Spacing } from '@kvika/audur-theme'
import React, { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { ProductInterestRatesSchema, ProductSchema } from '@kvika/audur-api-types'

import { GridItem, MainGridContainer } from '../components/grid-container'
import Layout from '../container/layout/Layout'
import { prismicConstants, SITE_ALERT_REVALIDATE_SECONDS } from '../utils/Constants'
import { ColumnClass, Gutters, MetaTagInfo, PageLayout, PreviewData, SiteAlert } from '../utils/types/Types'
import { getAudurApiClient, getAudurFooter, getAudurHeader, getAudurSiteAlert, getMetaTags } from '../utils/Utils'
import InterestTable from '../components/slices/InterestTable/InterestTable'
import CalculatorSlice from '../components/slices/Calculator/CalculatorSlice'
import MortgageFAQSlice from '../container/mortgage/mortgageFAQSlice'
import MortgageHeroCTASlice from '../container/mortgage/mortgageHeroCTASlice'
import AudurPrismicClient from '../prismic/prismicClient'
import {
	AudurContentPage,
	AudurContentPageConnectionConnection,
	AudurContentPageSlicesInteresttable,
	AudurFooter,
	AudurHeader,
	AudurMarketInterestRatesConnectionConnection,
	Maybe,
} from '../prismic/prismicTypes'
import { getNodesFromConnection, valueOrDefault } from '../prismic/prismicUtils'
import { renderEditorialItemGroup } from '../utils/editorial/renderEditorial'

import styles from './[uid].module.scss'

type Props = {
	audurContentPage: Maybe<AudurContentPage>
	historicalInterestRates: Array<ProductInterestRatesSchema>
	allAudurProducts: ProductSchema[]
	allAudurMarketInterestRates: Maybe<AudurMarketInterestRatesConnectionConnection>
	metaTags: MetaTagInfo
	footer: AudurFooter
	header: AudurHeader
	siteAlert: SiteAlert
}

const AudurContentsPage = ({
	audurContentPage,
	historicalInterestRates,
	allAudurProducts,
	allAudurMarketInterestRates,
	metaTags,
	footer,
	header,
	siteAlert,
}: Props) => {
	const [title, setTitle] = useState('')
	const isMobile = useMediaQuery({ query: '(max-width: 599px)' })
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 979px)' })

	useEffect(() => {
		if (audurContentPage && audurContentPage.title) {
			setTitle(audurContentPage.title)
		}
	}, [audurContentPage])

	return (
		<Layout title={title} metaTags={metaTags} footer={footer} header={header} siteAlert={siteAlert}>
			<>
				{audurContentPage?.editorialContentGroup?.map((group, index) => {
					return renderEditorialItemGroup(
						group,
						valueOrDefault(audurContentPage.pageLayout, PageLayout.Standard) as PageLayout,
						index,
						isMobile,
						isTabletOrMobile
					)
				})}
				{audurContentPage?.slices && (
					<>
						{audurContentPage.slices.map((slice) => {
							// Map __typename to slice type for proper component matching
							let sliceType = valueOrDefault(slice.type, '')
							if (!sliceType && slice.__typename) {
								const typename = slice.__typename as string
								switch (typename) {
									case 'AudurContentPageSlicesInteresttable':
										sliceType = 'interesttable'
										break
									case 'AudurContentPageSlicesCalculator':
										sliceType = 'calculator'
										break
									case 'AudurContentPageSlicesCalculator_faq':
										sliceType = 'calculator_faq'
										break
									case 'AudurContentPageSlicesHerowithcta':
										sliceType = 'herowithcta'
										break
									default:
										break
								}
							}

							// Render calculator_faq and calculator without container wrapper for full width
							if (sliceType === 'calculator_faq') {
								// Type assertion for new slice types that haven't been generated yet
								const faqSlice = slice as unknown as {
									primary?: Record<string, unknown>
									fields?: Array<Record<string, unknown>>
								}
								return (
									<MortgageFAQSlice
										key={`faq-${slice.type || slice.__typename}`}
										{...({
											primary: faqSlice.primary || {},
											fields: faqSlice.fields || [],
										} as unknown as React.ComponentProps<typeof MortgageFAQSlice>)}
									/>
								)
							}

							if (sliceType === 'calculator' && allAudurMarketInterestRates) {
								// Type assertion for calculator slice
								const calculatorSlice = slice as unknown as {
									type: 'calculator'
									primary?: {
										title?: string
										description?: string
										default_tab?: string
										background_color?: string
									}
								}
								return (
									<CalculatorSlice
										key={`calc-${slice.type || slice.__typename}`}
										slice={calculatorSlice}
										allAudurProducts={allAudurProducts}
										allAudurMarketInterestRates={allAudurMarketInterestRates}
									/>
								)
							}

							// Render other slices within the container
							return (
								<MainGridContainer
									key={`slice-${slice.type || slice.__typename}`}
									backgroundColor={Colors.Silver}
									gutters={Gutters.Both}
									className={styles.standardContentContainer}
								>
									<GridItem className={ColumnClass.All} style={{ marginBottom: Spacing.Large }}>
										{sliceType === 'interesttable' && slice.__typename === 'AudurContentPageSlicesInteresttable' && (
											<InterestTable
												tableFields={(slice as AudurContentPageSlicesInteresttable).fields}
												historicalInterestRates={historicalInterestRates}
											/>
										)}
										{sliceType === 'herowithcta' && (
											<MortgageHeroCTASlice
												{...({
													displayIconButton: false,
													hideImageMobile: false,
													firstButtonLink: '/stofna-reikning',
													secondButtonLink: '/husnaedislan',
													isSubpage: false,
													hasFAQ: false,
													...slice,
												} as unknown as React.ComponentProps<typeof MortgageHeroCTASlice>)}
											/>
										)}
									</GridItem>
								</MainGridContainer>
							)
						})}
					</>
				)}
			</>
		</Layout>
	)
}

interface Params extends ParsedUrlQuery {
	uid: string
}

export default AudurContentsPage

export const getStaticProps: GetStaticProps<Props, Params> = async ({ params, previewData, locale }) => {
	const uId = params ? params.uid : ''
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const audurContentPage = await audurClient.getAudurContentPageByUid({
		previewData: previewData as PreviewData,
		variables: { uId, lang: locale },
	})

	const [footer, header, siteAlert] = await Promise.all([getAudurFooter(), getAudurHeader(), getAudurSiteAlert()])

	const metaTags = getMetaTags({
		...audurContentPage,
		__typename: 'AudurContentPage',
	} as Maybe<AudurContentPage>)

	const apiClient = getAudurApiClient()
	const historicalInterestRates: Array<ProductInterestRatesSchema> = await apiClient.getHistoricalInterestRates()

	// Check if page contains calculator slice and fetch calculator data if needed
	const hasCalculatorSlice = audurContentPage?.slices?.some(
		(slice) => slice.type === 'calculator' || (slice.__typename as string) === 'AudurContentPageSlicesCalculator'
	)
	let allAudurProducts: ProductSchema[] = []
	let allAudurMarketInterestRates: Maybe<AudurMarketInterestRatesConnectionConnection> = null

	if (hasCalculatorSlice) {
		allAudurProducts = await apiClient.getProducts()
		allAudurMarketInterestRates = await audurClient.getMarketInterestRates({
			variables: { lang: prismicConstants.apiDefaultLocale },
		})
	}

	return {
		props: {
			audurContentPage,
			historicalInterestRates: uId === 'vaxtatafla' ? historicalInterestRates : [],
			allAudurProducts,
			allAudurMarketInterestRates,
			metaTags,
			footer,
			header,
			siteAlert,
		},
		revalidate: SITE_ALERT_REVALIDATE_SECONDS,
	}
}

export async function getStaticPaths() {
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const maybeContentPages: Maybe<AudurContentPageConnectionConnection> = await audurClient.getAllAudurContentPages()
	const contentPages: Array<AudurContentPage> = getNodesFromConnection(maybeContentPages)

	const paths =
		contentPages.map((contentPage) => {
			return {
				params: {
					uid: `${contentPage._meta.uid}`,
				},
				locale: `${contentPage._meta.lang}`,
			}
		}) || []

	return {
		paths,
		fallback: false,
	}
}
